import React, { useEffect, useState } from "react";
import { Check, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";

const FIELDS = [
  ["price_monthly_usd", "Monthly $", "number"], ["price_annual_usd", "Annual $", "number"],
  ["ai_insights_per_month", "AI insights / mo", "number"], ["max_profiles", "Profiles", "number"],
  ["max_reports_per_month", "Reports / mo", "number"], ["max_drafts_per_month", "Drafts / mo", "number"],
  ["max_checkins_per_month", "Check-ins / mo", "number"], ["deep_memory_enabled", "Deep memory", "bool"],
  ["priority_processing", "Priority AI", "bool"], ["voice_notes_enabled", "Voice notes", "bool"], ["active", "Live on /pricing", "bool"],
];
const inp = "w-full bg-ivory border border-champagne rounded-md h-9 px-2 text-sm text-cocoa outline-none focus:border-mauve";

export function AdminPlanPricing() {
  const [plans, setPlans] = useState([]);
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/governance/plans").then((r) => { setPlans(r.data.plans || []); setDirty({}); }).catch((e) => toast.error(getErrorMessage(e)));
  useEffect(() => { load(); }, []);

  const edit = (key, field, value) => {
    setPlans((ps) => ps.map((p) => (p.key === key ? { ...p, [field]: value } : p)));
    setDirty((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: value } }));
  };
  const save = async (key) => {
    setBusy(key);
    try {
      const patch = { ...dirty[key] };
      Object.keys(patch).forEach((k) => { if (typeof patch[k] === "string" && patch[k] !== "" && !isNaN(patch[k]) && !["name", "tagline"].includes(k)) patch[k] = Number(patch[k]); });
      await api.put(`/admin/governance/plans/${key}`, patch);
      toast.success(`${key} saved — live on /pricing, /billing and checkout`);
      await load();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-plan-pricing">
      <div className="ss-card p-4 text-sm text-cocoa/70 flex flex-wrap items-center justify-between gap-2">
        <span>Single source of truth: these five tiers drive the public <strong>/pricing</strong> page, <strong>/billing</strong>, Square checkout amounts (Plus → Sense+ SKUs, Luxe → Luxe SKUs) and every entitlement check. Members on the legacy <code>sense_plus</code> key inherit <strong>Plus</strong>.</span>
        <Link to="/pricing" target="_blank" className="ss-pill ss-pill-ghost h-9"><ExternalLink size={14} /> View public pricing</Link>
      </div>
      {/* Phones & tablets: one card per tier, fields in a two-column grid */}
      <div className="mt-4 space-y-3 lg:hidden" data-testid="plan-pricing-cards">
        {plans.map((p) => (
          <div key={p.key} className="ss-card p-4" data-testid={`plan-card-edit-${p.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <input value={p.name || ""} onChange={(e) => edit(p.key, "name", e.target.value)} className={`${inp} font-medium w-full`} aria-label="Tier name" />
                <input value={p.tagline || ""} onChange={(e) => edit(p.key, "tagline", e.target.value)} className={`${inp} mt-1 text-xs w-full`} placeholder="Tagline" aria-label="Tagline" />
                <p className="mt-1 text-[10px] uppercase tracking-widest text-cocoa/45">key · {p.key}</p>
              </div>
              <button disabled={!dirty[p.key] || busy === p.key} onClick={() => save(p.key)} className="ss-pill ss-pill-primary h-9 disabled:opacity-40 shrink-0">{busy === p.key ? "Saving…" : "Save"}</button>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-3">
              {FIELDS.map(([k, l, type]) => (
                <label key={k} className="block min-w-0">
                  <span className="block text-[10px] uppercase tracking-widest text-cocoa/50 leading-tight">{l}</span>
                  {type === "bool"
                    ? <button type="button" onClick={() => edit(p.key, k, !p[k])} className={`mt-1 h-9 w-14 rounded-full border transition-colors ${p[k] ? "bg-sage/70 border-sage" : "bg-ivory border-champagne"}`} aria-pressed={!!p[k]} aria-label={l}>{p[k] ? <Check size={13} className="mx-auto text-cocoa" /> : <span className="text-[10px] text-cocoa/40">off</span>}</button>
                    : <input type="number" min={0} value={p[k] ?? 0} onChange={(e) => edit(p.key, k, e.target.value)} className={`${inp} mt-1 w-full`} aria-label={l} />}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="ss-card mt-4 hidden lg:block overflow-x-auto">
        <table className="w-full text-left" data-testid="plan-pricing-table">
          <thead className="text-[10px] uppercase tracking-[0.25em] text-cocoa/50 border-b border-champagne">
            <tr><th className="px-3 py-2.5 font-medium min-w-[180px]">Tier</th>{FIELDS.map(([k, l]) => <th key={k} className="px-2 py-2.5 font-medium whitespace-nowrap">{l}</th>)}<th className="px-3 py-2.5" /></tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.key} className="border-b border-champagne/60 align-top" data-testid={`plan-row-${p.key}`}>
                <td className="px-3 py-3">
                  <input value={p.name || ""} onChange={(e) => edit(p.key, "name", e.target.value)} className={`${inp} font-medium`} data-testid={`plan-${p.key}-name`} />
                  <input value={p.tagline || ""} onChange={(e) => edit(p.key, "tagline", e.target.value)} className={`${inp} mt-1 text-xs`} placeholder="Tagline" />
                  <p className="mt-1 text-[10px] uppercase tracking-widest text-cocoa/45">key · {p.key}</p>
                </td>
                {FIELDS.map(([k, , type]) => (
                  <td key={k} className="px-2 py-3">
                    {type === "bool"
                      ? <button type="button" onClick={() => edit(p.key, k, !p[k])} data-testid={`plan-${p.key}-${k}`} className={`h-9 w-14 rounded-full border transition-colors ${p[k] ? "bg-sage/70 border-sage" : "bg-ivory border-champagne"}`} aria-pressed={!!p[k]}>{p[k] ? <Check size={14} className="mx-auto text-cocoa" /> : <span className="text-xs text-cocoa/40">off</span>}</button>
                      : <input type="number" min={0} value={p[k] ?? 0} onChange={(e) => edit(p.key, k, e.target.value)} data-testid={`plan-${p.key}-${k}`} className={`${inp} w-24`} />}
                  </td>
                ))}
                <td className="px-3 py-3">
                  <button disabled={!dirty[p.key] || busy === p.key} onClick={() => save(p.key)} data-testid={`plan-${p.key}-save`} className="ss-pill ss-pill-primary h-9 disabled:opacity-40">{busy === p.key ? "Saving…" : "Save"}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
